import { Message } from './chatService';

interface StoredChat {
  messages: Message[];
  timestamp: number;
  expiresAt: number;
}

const STORAGE_KEY = 'poker_chat_history';

const EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const MAX_MESSAGES = 100;

// Sensitive patterns to detect
const SENSITIVE_PATTERNS = [
  // Credit card patterns
  /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/,
  /\b\d{13,19}\b/,

  // Password-related keywords
  /password\s*[:=]\s*\S+/i,
  /pass\s*[:=]\s*\S+/i,
  /pwd\s*[:=]\s*\S+/i,

  // SSN patterns
  /\b\d{3}-\d{2}-\d{4}\b/,

  // Common sensitive keywords
  /credit\s*card/i,
  /cvv/i,
  /security\s*code/i,
  /expir(y|ation)\s*date/i,
];

class ChatStorageService {
  /**
   * Check if a message contains sensitive information
   */
  private containsSensitiveInfo(content: string): boolean {
    return SENSITIVE_PATTERNS.some(pattern => pattern.test(content));
  }

  /**
   * Check if storage has expired
   */
  private isExpired(expiresAt: number): boolean {
    return Date.now() > expiresAt;
  }

  /**
   * Sanitize messages by removing sensitive ones
   */
  private sanitizeMessages(messages: Message[]): Message[] {
    return messages.filter(msg => !this.containsSensitiveInfo(msg.content));
  }

  /**
   * Limit messages to MAX_MESSAGES
   */
  private limitMessages(messages: Message[]): Message[] {
    if (messages.length > MAX_MESSAGES) {
      // Keep the first message (welcome) and the most recent messages
      const welcomeMessage = messages[0];
      const recentMessages = messages.slice(-(MAX_MESSAGES - 1));
      return [welcomeMessage, ...recentMessages];
    }
    return messages;
  }

  /**
   * Save chat history to localStorage
   */
  saveChat(messages: Message[]): boolean {
    try {
      // Don't save if there's only the welcome message
      if (messages.length <= 1) {
        return false;
      }

      // Sanitize and limit messages
      const sanitized = this.sanitizeMessages(messages);
      const limited = this.limitMessages(sanitized);

      const chatData: StoredChat = {
        messages: limited,
        timestamp: Date.now(),
        expiresAt: Date.now() + EXPIRY_TIME,
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(chatData));
      return true;
    } catch (error) {
      console.error('Failed to save chat history:', error);
      return false;
    }
  }

  /**
   * Load chat history from localStorage
   */
  loadChat(): Message[] | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);

      if (!stored) {
        return null;
      }

      const chatData: StoredChat = JSON.parse(stored);

      // Check if expired
      if (this.isExpired(chatData.expiresAt)) {
        this.clearChat();
        return null;
      }

      // Convert timestamp strings back to Date objects
      const messages = chatData.messages.map(msg => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      }));

      return messages;
    } catch (error) {
      console.error('Failed to load chat history:', error);
      this.clearChat(); // Clear corrupted data
      return null;
    }
  }

  /**
   * Clear chat history from localStorage
   */
  clearChat(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear chat history:', error);
    }
  }

  /**
   * Check if a specific message should be persisted
   */
  shouldPersistMessage(content: string): boolean {
    return !this.containsSensitiveInfo(content);
  }

  /**
   * Get storage info (for debugging/display)
   */
  getStorageInfo(): {
    hasChat: boolean;
    messageCount: number;
    expiresIn: number | null;
  } {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);

      if (!stored) {
        return { hasChat: false, messageCount: 0, expiresIn: null };
      }

      const chatData: StoredChat = JSON.parse(stored);
      const expiresIn = Math.max(0, chatData.expiresAt - Date.now());

      return {
        hasChat: true,
        messageCount: chatData.messages.length,
        expiresIn: expiresIn,
      };
    } catch (error) {
      return { hasChat: false, messageCount: 0, expiresIn: null };
    }
  }
}

export const chatStorageService = new ChatStorageService();
